import React, { type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { labelContract } from './label.contract'

// Figma draws the required marker as its own text node beside the label.
const REQUIRED_MARK = '*'

export const LABEL_VARIANT = variantForStyleId(labelContract.textStyleIds[0])

export type LabelProps = {
    children: ReactNode
    required?: boolean
    className?: string
    testID?: string
}

/**
 * Written by hand rather than generated: three nodes, and the contract carries
 * the tokens instead of the layout. `label.contract.spec.ts` asserts these
 * classes resolve to exactly the tokens the Figma node binds.
 *
 * The web node pads itself horizontally and nowhere else, which lines the text
 * up with the field below it. The mobile twin (`7001:17297`) pads nothing, hangs
 * a Plus icon off the row and sets the label in Medium — this package held that
 * one by mistake until the node was checked against `[Web] Primitives`.
 *
 * No gluestack — a label supplies no behaviour to wrap.
 */
export const Label = ({ children, required, className, testID }: LabelProps) => (
    <View
        className={twMerge('flex-row items-center gap-2xs px-xs', className)}
        testID={testID ?? 'label'}
    >
        <Typography variant={LABEL_VARIANT} tone='default'>
            {children}
        </Typography>
        {required ? (
            <Typography variant={LABEL_VARIANT} tone='default' testID='label-asterisk'>
                {REQUIRED_MARK}
            </Typography>
        ) : null}
    </View>
)

Label.displayName = 'Label'
